import React, { useState, useEffect, useReducer } from "react";
import Grid from "@mui/material/Grid";
import List from "@mui/material/List";
import Card from "@mui/material/Card";
import CardHeader from "@mui/material/CardHeader";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import { get } from "aws-amplify/api";
import currentUserToken from "CurrentUserToken";
import { APIStatusDialog } from "components/APIStatusDialog";
import { I18n } from "aws-amplify/utils";
import { useTemplateController } from "context";
const reducer = (lastState, action) => {
  if (action.type === "DATA_RECEIVED") {
    return { audit: action.value, fetched: true };
  }
  return lastState;
};

function not(a, b) {
  return a.filter((value) => !b.includes(value));
}

function intersection(a, b) {
  return a.filter((value) => b.includes(value));
}

export default function SelectAllTransferList({
  hubsTotal,
  hubs,
  profileUser,
  user_id,
  onClose,
}) {
  const [state, dispatch] = useReducer(reducer, { audit: [], fetched: false });
  const [checked, setChecked] = useState([]);
  const [left, setLeft] = useState(hubs);
  const [right, setRight] = useState(hubsTotal);
  const [apiStatus, setApiStatus] = useState({
    open: false,
    status: "",
    message: "",
  });
  const [loading, setLoading] = useState(false); // State for loading

  useEffect(() => {
    setLeft(hubs);
  }, [hubs]);

  useEffect(() => {
    setRight(hubsTotal);
  }, [hubsTotal]);

  const leftChecked = intersection(checked, left);
  const rightChecked = intersection(checked, right);

  const handleToggle = (value) => () => {
    setChecked((prevChecked) => 
      prevChecked.includes(value) 
        ? prevChecked.filter((item) => item !== value) // Se l'elemento è presente, rimuovilo
        : [...prevChecked, value] // Se non è presente, aggiungilo
    );
  };
  
  const [controller] = useTemplateController();
  const { darkMode } = controller;

  const numberOfChecked = (items) => checked.length;

  const handleToggleAll = (items) => () => {};

  const tlmPostGroupAssignment = async (groupName, remove) => {
    try {
      setApiStatus({
        open: true,
        status: "pending",
        message: I18n.get("Updating the user"),
      });

      const restOperation = get({
        apiName: "TelemonitoringAPI",
        path: "/userManagement/group/assignment",
        options: {
          headers: {
            "Content-Type": "application/json",
            Authorization: `${await currentUserToken()}`,
          },
          queryParams: {
            group_name: groupName,
            user_id: user_id,
            remove: remove ? "false" : "true",
          },
        },
      });
      const response = await restOperation.response;
      if (response.statusCode === 200) {
        setApiStatus({
          open: true,
          status: "success",
          message: I18n.get("User successfully updated"),
        });

      } else {
        setApiStatus({
          open: true,
          status: "error",
          message: I18n.get("Failed to update user"),
        });
      }
      setLoading(false);
      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
        onClose();
      }, 2000);
    } catch (error) {
      setApiStatus({
        open: true,
        status: "error",
        message: I18n.get("Failed to update user"),
      });
      setLoading(false); // Set loading to false on error
      setTimeout(() => {
        setApiStatus((prevState) => ({ ...prevState, open: false }));
      }, 2000);
    }
  };

  const handleCheckedRight = async () => {
    setLoading(true); // Set loading to true
    setRight(right.concat(leftChecked));
    setLeft(not(left, leftChecked));
    setChecked(not(checked, leftChecked));

    for (const hub of leftChecked) {
      await tlmPostGroupAssignment(hub, false); // Add assignment
    }
  };

  const handleCheckedLeft = async () => {
    setLoading(true); // Set loading to true
    setLeft(left.concat(rightChecked));
    setRight(not(right, rightChecked));
    setChecked(not(checked, rightChecked));

    for (const hub of rightChecked) {
      await tlmPostGroupAssignment(hub, true); // Remove assignment
    }
  };
  useEffect(() => {
    // Filter hubsTotal so that it doesn't include items present in hubs (left)
    const filteredRight = hubsTotal.filter((item) => !hubs.includes(item));
    setRight(filteredRight);
  }, [hubsTotal, hubs]);

  const customList = (title, items = []) => (
    <Card sx={{ border: "2px solid black" }}>
      <CardHeader
        sx={{ px: 2, py: 1 }}
        avatar={null}
        title={title}
      />
      <Divider />
      <List
        sx={{
          width: 350,
          height: 250,
          bgcolor: darkMode? "#1f2020": "background.paper",
          overflow: "auto",
        }}
        dense
        role="list"
      >
        {items.map((value, index) => {
          const labelId = `transfer-list-item-${index}-label`;

          return (
            <ListItemButton key={index} onClick={handleToggle(value)}>
              <ListItemIcon>
                <Checkbox
                  checked={checked.indexOf(value) !== -1}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{ "aria-labelledby": labelId }}
                />
              </ListItemIcon>
              <ListItemText id={labelId} primary={value} />
            </ListItemButton>
          );
        })}
      </List>
    </Card>
  );

  return (
    <>
      {profileUser.includes("Admin") && (
        <Grid container spacing={2} justifyContent="center" alignItems="center">
          <Grid item>{customList(I18n.get("Hubs Assigned"), left)}</Grid>
          <Grid item>
            <Grid container direction="column" alignItems="center">
              <Button
                onClick={handleCheckedLeft}
                disabled={rightChecked.length === 0}
                aria-label="move selected left"
              >
                &lt;
              </Button>
              <Button
                onClick={handleCheckedRight}
                disabled={leftChecked.length === 0}
                aria-label="move selected right"
              >
                &gt;
              </Button>
            </Grid>
          </Grid>
          <Grid item>{customList(I18n.get("Hubs Total"), right)}</Grid>
        </Grid>
      )}
      {(profileUser.includes("Operator") || profileUser.includes("Guest")) && (
        <Grid container spacing={2} justifyContent="center" alignItems="center">
          <Grid item>{customList(I18n.get("Clusters Assigned"), left)}</Grid>
          <Grid item>
            <Grid container direction="column" alignItems="center">
              <Button
                onClick={handleCheckedRight}
                disabled={leftChecked.length === 0}
                aria-label="move selected right"
              >
                &gt;
              </Button>
              <Button
                onClick={handleCheckedLeft}
                disabled={rightChecked.length === 0}
                aria-label="move selected left"
              >
                &lt;
              </Button>
            </Grid>
          </Grid>
          <Grid item>{customList(I18n.get("Clusters Total"), right)}</Grid>
        </Grid>
      )}
      {loading && <CircularProgress />}
      <APIStatusDialog
        open={apiStatus.open}
        status={apiStatus.status}
        message={apiStatus.message}
      />
    </>
  );
}
